/**
 * QA Panel — Rebate controller (Campaigns / Rebate sub-tab).
 *
 * Mirrors `prizePopController.ts` and `campaignsController.ts` but targets
 * the rebate campaign type. Lets QA take over the rebate slice for an
 * editing session — fetch a real list with pasted creds, edit entries in
 * place, and inject synthetic mocks alongside them.
 *
 * Flow:
 *   - `enableMocking()` sets `campaignsStore.mockMode = true`. The companion
 *     guard in `setCampaignsFromResponse` and the gates in `useCampaignsQuery`
 *     then drop real refetches.
 *   - `fetchExternal(sessionKey, playerId)` runs a one-off `fetch()` against
 *     the public Rebate endpoint with QA-pasted creds and seeds the store
 *     via `seedExternalCampaigns` (which bypasses the mockMode early-return
 *     that blocks `setCampaignsFromResponse`).
 *   - `createMockCampaign(form)` appends a synthetic campaign with id
 *     `qa-mock-<timestamp>`. mockMode is NOT auto-flipped — the UI gates
 *     this behind enableMocking, so the flag is already on by the time we
 *     get here.
 *   - `updateCampaignField(id, patch)` patches a single campaign in place
 *     (no resort).
 *   - `disableMocking()` clears every mock and disables `mockMode` so real
 *     polling resumes; in-place edits to real entries are silently lost on
 *     the next poll, which is expected.
 *
 * Boundary note: imports from `features/campaigns` strictly via the public
 * `index.ts`. The qaPanel build flag is dead-stripped in prod; we therefore
 * must NOT add any imports the other way (campaigns → qaPanel).
 */

import { useCampaignsStore, REBATE_CAMPAIGN_TYPE, isRebateSource, RebateStatuses } from '../../campaigns';
import type {
  CampaignSource,
  IRebateCampaign,
  IRebateResources,
  CampaignLevel,
  ICashbackLevel,
  IUserProgress,
} from '../../campaigns';
import { useAppStore } from 'store/app/appStore';
import { buildPortalAssetUrl } from 'shared/utils/helpers';

/* ── Public types surfaced to the popup ────────────────────────────── */

export interface CreateMockRebateForm {
  name: string;
  terms: string;
  status: RebateStatuses;
  level: CampaignLevel;
  baseCurrency: string;
  /** All offsets are in MINUTES relative to "now" at submit time. */
  startOffsetMin: number;
  endOffsetMin: number;
  minWinBetAmount: number;
  /** Game keys to include. */
  games: string[];
  /** 1–3 cashback levels configured in the popup. Falls back to the defaults when missing/empty. */
  cashbackLevels?: ICashbackLevel[];
  joined: boolean;
  /** Only used when `joined` is true — drives the computed `userProgress`. */
  spentBetAmount: number;
}

export interface MockedRebateRef {
  id: string;
  name: string;
}

/**
 * Compact, transferable view of a Rebate campaign for the popup.
 */
export interface RebateCampaignSummary {
  id: string;
  name: string;
  terms: string;
  status: RebateStatuses;
  level: CampaignLevel;
  baseCurrency: string;
  startDate: string;
  endDate: string;
  visibleEndDate: string;
  maxPayoutAmount: number;
  minWinBetAmount: number;
  joined: boolean;
  games: string[];
  /** Copy of the campaign's levels; defaults when missing/empty. */
  cashbackLevels: ICashbackLevel[];
  /** `userProgress.spentBetAmount`, or 0 when there is no progress. */
  spentBetAmount: number;
  /** True when the id starts with `'qa-mock-'`. */
  isMock: boolean;
}

export interface RebateState {
  /** Whether the campaigns store is currently in mock mode. */
  mockMode: boolean;
  /** Light list of campaigns injected via this panel. */
  mocks: MockedRebateRef[];
  /** Editable summaries of every Rebate campaign currently in the store. */
  campaigns: RebateCampaignSummary[];
  /** Best guess at the player's currency at the time of read. */
  defaultBaseCurrency: string;
}

export interface FetchExternalResult {
  ok: boolean;
  /** HTTP status; 0 means network/CORS error, no response received. */
  status?: number;
  message?: string;
  /** Number of campaigns seeded on success. */
  count?: number;
}

/* ── Hard-coded reference data ─────────────────────────────────────── */

/**
 * Resource bundle for synthetic Rebate campaigns.
 * Uses `buildPortalAssetUrl` to resolve images from the local assets
 * directory (`apps/assets/public/assets/portal/campaigns/rebate/Default/`),
 * served by Storybook's staticDirs and by the portal CDN in dev/prod.
 */
const FILL_COLOR = '#06230C';
const BORDER_COLOR = '#2F980F';

const res = (key: string) => buildPortalAssetUrl(`/campaigns/rebate/Default/${key}.webp`);

function buildMockResources(): IRebateResources {
  return {
    CampaignsMultiViewSoloSeparatedBackgroundLandscape: res('CampaignsMultiViewSoloSeparatedBackgroundLandscape'),
    CampaignsMultiViewSoloSeparatedBackgroundPortrait: res('CampaignsMultiViewSoloSeparatedBackgroundPortrait'),
    CampaignsMultiViewSoloTransparentImage: res('CampaignsMultiViewSoloTransparentImage'),
    CampaignsMultiViewSoloTransparentImageDesktop: res('CampaignsMultiViewSoloTransparentImageDesktop'),
    CampaignsPlate: res('CampaignsPlate'),
    CampaignsPlateClose: res('CampaignsPlateClose'),
    CampaignsPlateCloseLogo: res('CampaignsPlateCloseLogo'),
    CampaignsSingleViewSoloBackgroundClose: res('CampaignsSingleViewSoloBackgroundClose'),
    CampaignsSingleViewSoloBackgroundLandscape: res('CampaignsSingleViewSoloBackgroundLandscape'),
    CampaignsSingleViewSoloBackgroundPortrait: res('CampaignsSingleViewSoloBackgroundPortrait'),
    CampaignsMultiViewPlate: res('CampaignsMultiViewPlate'),
    FillColor: FILL_COLOR,
    BorderColor: BORDER_COLOR,
  };
}

/**
 * Default game keys for mock rebate campaigns.
 */
const MOCK_REBATE_GAMES: string[] = [
  'ARSlot',
  'BCSlot',
  'BCBSlot',
  'FBHRSlot',
  'FVBPSlot',
  'FBRSlot',
  'FVCLHSlot',
  'FVCHTSlot',
  'FVCHSlot',
  'FDCSlot',
  'FDHSlot',
  'FVDRSlot',
  'FVJHSlot',
  'FVLRSlot',
  'FVSHRSlot',
  'FVSHSlot',
  'FVSFSlot',
  'FVSPSlot',
  'FVSRSlot',
  'TADSlot',
  'TBHRSlot',
  'TNBPSlot',
  'TNBRSlot',
  'TNCLHSlot',
  'TCHSlot',
  'TCHBSlot',
  'TCWSlot',
  'TNDCSlot',
  'TNDHSlot',
  'TNDRSlot',
  'TNJHSlot',
  'TNJRSlot',
  'TNLHSlot',
  'TNLRSlot',
  'TSDSlot',
  'TNSHRSlot',
  'TNSHSlot',
  'TNSFSlot',
  'TNSPSlot',
  'TNSRSlot',
  'OBCSlot',
  'OBHSlot',
  'OHBPSlot',
  'OHBRSlot',
  'OHCLHSlot',
  'OHCHTSlot',
  'OHCHSlot',
  'OSFSlot',
  'OSHSlot',
  'OSHSSlot',
];

/* ── Form-default helpers ──────────────────────────────────────────── */

// fallow-ignore-next-line unused-export
export function getDefaultBaseCurrency(): string {
  return useAppStore.getState().balance?.currency ?? 'EUR';
}

// fallow-ignore-next-line unused-export
export function buildDefaultRebateForm(): CreateMockRebateForm {
  const baseCurrency = getDefaultBaseCurrency();
  return {
    name: 'QA Test Rebate',
    terms: '',
    status: RebateStatuses.ACTIVE,
    level: 'PURPLE',
    baseCurrency,
    startOffsetMin: 0,
    endOffsetMin: 240,
    minWinBetAmount: 300,
    games: MOCK_REBATE_GAMES.slice(),
    joined: false,
    spentBetAmount: 0,
  };
}

/**
 * Default 3 cashback levels (values from the original mock / Storybook
 * fixture), chained so level N+1 `betRangeFrom` = level N `betRangeTo` + 0.01.
 * Mirrored by `rbDefaultForm` in the popup.
 */
export function buildDefaultCashbackLevels(): ICashbackLevel[] {
  return withComputedMaxPayout([
    { levelNumber: 1, betRangeFrom: 0, betRangeTo: 1000, rebatePercent: 1, maxPayoutAmount: 0 },
    { levelNumber: 2, betRangeFrom: 1000.01, betRangeTo: 7000, rebatePercent: 1.5, maxPayoutAmount: 0 },
    { levelNumber: 3, betRangeFrom: 7000.01, betRangeTo: 13000, rebatePercent: 2.0, maxPayoutAmount: 0 },
  ]);
}

/**
 * maxPayoutAmount = (betRangeTo − betRangeFrom) × rebatePercent / 100,
 * rounded to 2 decimals, clamped at 0. Mirrored by `rbComputeMax` in the popup;
 * this side is the source of truth for anything that reaches the store.
 */
function computeLevelMaxPayout(level: ICashbackLevel): number {
  const raw = ((level.betRangeTo - level.betRangeFrom) * level.rebatePercent) / 100;
  return raw > 0 ? Math.round(raw * 100) / 100 : 0;
}

function withComputedMaxPayout(levels: ICashbackLevel[]): ICashbackLevel[] {
  return levels.map((l) => ({ ...l, maxPayoutAmount: computeLevelMaxPayout(l) }));
}

/** Campaign-level maxPayoutAmount = sum of the levels' maxPayoutAmount, 2 decimals. */
function sumLevelMaxPayout(levels: ICashbackLevel[]): number {
  return Math.round(levels.reduce((sum, l) => sum + l.maxPayoutAmount, 0) * 100) / 100;
}

/**
 * Synthesize `userProgress` for a joined mock from the spent amount + levels.
 *   - current level = highest level whose `betRangeFrom` was reached (local
 *     twin of campaigns' `findCurrentLevel`, which is not on the public API)
 *   - `levelProgress` = integer 0..100 through the current level's range
 *     (0 below level 1, 100 once past its `betRangeTo`)
 *   - `claimedWin` = sum of `maxPayoutAmount` of finished levels
 *     (spent >= `betRangeTo`), no `minWinBetAmount` gate; `totalWinAmount` mirrors it.
 */
export function buildMockUserProgress(spentBetAmount: number, levels: ICashbackLevel[]): IUserProgress {
  const spent = Number.isFinite(spentBetAmount) ? spentBetAmount : 0;
  const byFrom = [...levels].sort((a, b) => a.betRangeFrom - b.betRangeFrom);
  const current = [...byFrom].reverse().find((l) => spent >= l.betRangeFrom);

  let levelProgress = 0;
  if (current) {
    const span = current.betRangeTo - current.betRangeFrom;
    const raw = span > 0 ? ((spent - current.betRangeFrom) / span) * 100 : 100;
    levelProgress = Math.min(100, Math.max(0, Math.round(raw)));
  }

  const finishedSum = byFrom.filter((l) => spent >= l.betRangeTo).reduce((sum, l) => sum + l.maxPayoutAmount, 0);
  const claimedWin = Math.round(finishedSum * 100) / 100;

  return { spentBetAmount: spent, levelProgress, claimedWin, totalWinAmount: claimedWin };
}

/* ── Campaign builder ──────────────────────────────────────────────── */

function offsetIso(now: number, offsetMin: number): string {
  return new Date(now + offsetMin * 60 * 1000).toISOString();
}

/**
 * Build a fully-formed `IRebateCampaign` ready to be pushed through
 * `addMockCampaign`.
 */
// fallow-ignore-next-line unused-export
export function buildMockRebateCampaign(form: CreateMockRebateForm): IRebateCampaign {
  const id = 'qa-mock-' + Date.now().toString();
  const now = Date.now();
  const startDate = offsetIso(now, form.startOffsetMin);
  const endDate = offsetIso(now, form.endOffsetMin);

  const cashbackLevels =
    Array.isArray(form.cashbackLevels) && form.cashbackLevels.length > 0
      ? withComputedMaxPayout(form.cashbackLevels.slice(0, 3).map((l, i) => ({ ...l, levelNumber: i + 1 })))
      : buildDefaultCashbackLevels();

  return {
    id,
    name: form.name,
    terms: form.terms,
    status: form.status,
    startDate,
    endDate,
    visibleEndDate: endDate,
    maxPayoutAmount: sumLevelMaxPayout(cashbackLevels),
    level: form.level,
    games: form.games.slice(),
    minStake: 25,
    baseCurrency: form.baseCurrency,
    joined: form.joined,
    resources: buildMockResources(),
    // Obsolete (TODO_Rebate in rebate.types.ts) but still required by the type.
    minPayoutAmount: 0,
    minWinBetAmount: form.minWinBetAmount,
    userProgress: form.joined ? buildMockUserProgress(form.spentBetAmount, cashbackLevels) : null,
    cashbackLevels,
    type: REBATE_CAMPAIGN_TYPE,
  };
}

/* ── Controller ───────────────────────────────────────────────────── */

export class RebateController {
  private onChange: (() => void) | null = null;
  private storeUnsubscribe: (() => void) | null = null;

  init(onChange: () => void): void {
    this.onChange = onChange;
    this.storeUnsubscribe = useCampaignsStore.subscribe((state, prev) => {
      if (state.mockMode !== prev.mockMode || state.campaigns !== prev.campaigns) {
        this.onChange?.();
      }
    });
  }

  destroy(): void {
    this.storeUnsubscribe?.();
    this.storeUnsubscribe = null;
    this.onChange = null;
  }

  getState(): RebateState {
    const store = useCampaignsStore.getState();
    const rebateList = store.campaigns.filter(isRebateSource) as IRebateCampaign[];
    const mocks: MockedRebateRef[] = rebateList
      .filter((c) => c.id.startsWith('qa-mock-'))
      .map((c) => ({ id: c.id, name: c.name }));
    const campaigns: RebateCampaignSummary[] = rebateList.map(toSummary);
    return {
      mockMode: store.mockMode,
      mocks,
      campaigns,
      defaultBaseCurrency: getDefaultBaseCurrency(),
    };
  }

  /**
   * Enable mockMode without touching the campaign list. The companion
   * guards in `setCampaignsFromResponse` and `useCampaignsQuery` then
   * drop real refetches; the popup is free to seed/edit/add at will.
   */
  enableMocking(): void {
    useCampaignsStore.getState().setMockMode(true);
  }

  /**
   * Disable mockMode + clear every mock entry. Real polling resumes; the
   * next refetch silently overwrites any in-place edits to real entries —
   * that's expected (the editing session is over).
   */
  disableMocking(): void {
    useCampaignsStore.getState().clearMockCampaigns();
  }

  /**
   * One-shot, QA-driven external fetch using the user-pasted creds. The
   * URL is hard-coded to the public `rebate-service-qa-ext.egt-ong.com`
   * host — this does NOT share auth with the lobby's normal campaigns
   * fetch path. On success the store is seeded via `seedExternalCampaigns`,
   * which is the only door not gated by mockMode.
   *
   * Endpoint analog: `tournaments-ong-qa-ext.egt-ong.com` for tournaments;
   * `prizepop-service-qa-ext.egt-ong.com` for Prize Pop.
   * Host and path derived from `rebateConfig` + the default `rebateHost`
   * baked into `appStore` / `iframe-loader`.
   */
  // TODO_Rebate: Check if the endpoint is correct and if the query params are correct once the BE is ready.
  async fetchExternal(sessionKey: string, playerId: string): Promise<FetchExternalResult> {
    const url =
      'https://rebate-service-qa-ext.egt-ong.com/rebateservice/api/game-client/rebates/get-all' +
      `?sessionKey=${encodeURIComponent(sessionKey)}&playerId=${encodeURIComponent(playerId)}` +
      '&stripResourcesHost=true';

    let response: Response;
    try {
      response = await fetch(url, { mode: 'cors' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      return { ok: false, status: 0, message };
    }

    if (!response.ok) {
      let message = response.statusText || 'Request failed';
      try {
        const body = (await response.json()) as { code?: string; message?: string };
        if (body && typeof body.message === 'string' && body.message.length > 0) {
          message = body.message;
        }
      } catch {
        /* ignore — non-JSON body */
      }
      return { ok: false, status: response.status, message };
    }

    let body: { campaigns?: CampaignSource[] };
    try {
      body = (await response.json()) as { campaigns?: CampaignSource[] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      return { ok: false, status: response.status, message };
    }

    const list = Array.isArray(body.campaigns) ? body.campaigns : [];
    useCampaignsStore.getState().seedExternalCampaigns(list);
    return { ok: true, status: response.status, count: list.length };
  }

  /**
   * Build a campaign from the form payload and inject it. Returns the
   * generated id so the popup can reflect "just created" feedback.
   */
  createMockCampaign(form: CreateMockRebateForm): string {
    const campaign = buildMockRebateCampaign(form);
    useCampaignsStore.getState().addMockCampaign(campaign as never);
    return campaign.id;
  }

  /** Remove a single mocked campaign by id. */
  removeMockCampaign(id: string): void {
    if (!id.startsWith('qa-mock-')) return;
    useCampaignsStore.getState().removeCampaign(id);
  }

  /**
   * In-place patch of a single campaign (real or mocked). Thin wrapper
   * around the store's `updateCampaignById` action — does NOT resort.
   */
  updateCampaignField(id: string, patch: Partial<IRebateCampaign>): void {
    // Campaign maxPayoutAmount is derived from the levels only — never taken
    // from the patch, and left untouched when the levels aren't patched.
    const next: Partial<IRebateCampaign> = { ...patch };
    delete next.maxPayoutAmount;
    if (patch.cashbackLevels) {
      next.cashbackLevels = withComputedMaxPayout(patch.cashbackLevels);
      next.maxPayoutAmount = sumLevelMaxPayout(next.cashbackLevels);
    }
    // The popup only sends `userProgress.spentBetAmount`; recompute the rest
    // against the recomputed patched levels (or the campaign's current ones).
    if (patch.userProgress) {
      const existing = useCampaignsStore
        .getState()
        .campaigns.filter(isRebateSource)
        .find((c) => c.id === id);
      const levels = next.cashbackLevels ?? existing?.cashbackLevels ?? [];
      next.userProgress = buildMockUserProgress(patch.userProgress.spentBetAmount, levels);
    }
    useCampaignsStore.getState().updateCampaignById(id, next);
  }

  /** Clear every mock and resume real polling. */
  clearAll(): void {
    useCampaignsStore.getState().clearMockCampaigns();
  }
}

/* ── Summary helpers ──────────────────────────────────────────────── */

function toSummary(c: IRebateCampaign): RebateCampaignSummary {
  return {
    id: c.id,
    name: c.name,
    terms: c.terms,
    status: c.status,
    level: c.level,
    baseCurrency: c.baseCurrency,
    startDate: c.startDate,
    endDate: c.endDate,
    visibleEndDate: c.visibleEndDate,
    maxPayoutAmount: c.maxPayoutAmount,
    minWinBetAmount: c.minWinBetAmount,
    joined: c.joined,
    spentBetAmount: c.userProgress?.spentBetAmount ?? 0,
    games: Array.isArray(c.games) ? c.games.slice() : [],
    cashbackLevels:
      Array.isArray(c.cashbackLevels) && c.cashbackLevels.length > 0
        ? c.cashbackLevels.map((l) => ({ ...l }))
        : buildDefaultCashbackLevels(),
    isMock: c.id.startsWith('qa-mock-'),
  };
}
